import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useBrakePadSeverity, {BrakeEnergyLevel} from '@di-shared/features/diag/brake/useBrakePadSeverity';
import {formatNumberWithCommas} from '@di-shared/utils';
import React from 'react';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import ImageSelector from '@di-shared/features/common/ImageSelector';

interface BrakeEnergyContentsProps {
  vin: string;
}

function BrakeEnergyContents({vin}: BrakeEnergyContentsProps) {
  const {getEnergyLevel, energyUnit} = useBrakePadSeverity({vin});
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.brakePad.getBrake1), vin],
    queryFn: () => diApi.brakePad.getBrake1({vin}),
    select: ({data}) => data,
  });
  if (!data) {
    return <BrakeEnergyContentsNoData />;
  }
  const energyLevel = getEnergyLevel(data.onOffEnergySum);

  switch (energyLevel) {
    case BrakeEnergyLevel.LEVEL1:
      return (
        <BrakeEnergyContents1
          onOffEnergySum={data.onOffEnergySum}
          energyUnit={energyUnit}
        />
      );
    case BrakeEnergyLevel.LEVEL2:
      return (
        <BrakeEnergyContents2
          onOffEnergySum={data.onOffEnergySum}
          energyUnit={energyUnit}
        />
      );
    case BrakeEnergyLevel.LEVEL3:
    default:
      return (
        <BrakeEnergyContents3
          onOffEnergySum={data.onOffEnergySum}
          energyUnit={energyUnit}
        />
      );
  }
}

export default BrakeEnergyContents;

interface BrakeEnergyContents {
  onOffEnergySum: number;
  energyUnit: string;
}

function BrakeEnergyContents1({onOffEnergySum, energyUnit}: BrakeEnergyContents) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3
          className="title_xxsmall"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30067')}
        </h3>
      </div>
      <div className="braking_energy">
        <p
          className="braking_energy_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30072_$energy', {energy: formatNumberWithCommas(Math.round(onOffEnergySum)) + energyUnit})}
        </p>
        <div className="braking_energy_imgbox">
          <ImageSelector
            className="braking_energy_img"
            alt="braking_energy_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ice.png')}
          />
        </div>
      </div>
    </div>
  );
}

function BrakeEnergyContents2({onOffEnergySum, energyUnit}: BrakeEnergyContents) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3
          className="title_xxsmall"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30068')}
        </h3>
      </div>
      <div className="braking_energy">
        <p
          className="braking_energy_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30072_$energy', {energy: formatNumberWithCommas(Math.round(onOffEnergySum)) + energyUnit})}
        </p>
        <div className="braking_energy_imgbox">
          <ImageSelector
            className="braking_energy_img"
            alt="braking_energy_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_fried_egg.png')}
          />
        </div>
      </div>
    </div>
  );
}

function BrakeEnergyContents3({onOffEnergySum, energyUnit}: BrakeEnergyContents) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3
          className="title_xxsmall"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30069')}
        </h3>
      </div>
      <div className="braking_energy">
        <p
          className="braking_energy_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30072_$energy', {energy: formatNumberWithCommas(Math.round(onOffEnergySum)) + energyUnit})}
        </p>
        <div className="braking_energy_imgbox">
          <ImageSelector
            className="braking_energy_img"
            alt="braking_energy_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_ramen.png')}
          />
        </div>
      </div>
    </div>
  );
}

export function BrakeEnergyContentsNoData() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3
          className="title_xxsmall"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30070')}
        </h3>
      </div>
      <div className="braking_energy">
        <p
          className="braking_energy_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30073')}
        </p>
        <div className="braking_energy_imgbox">
          <ImageSelector
            className="braking_energy_img"
            alt="braking_energy_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
          />
        </div>
      </div>
    </div>
  );
}

export function BrakeEnergyContentsError() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3
          className="title_xxsmall"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30071')}
        </h3>
      </div>
      <div className="braking_energy">
        <p
          className="braking_energy_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30072_$energy', {energy: '-'})}
        </p>
        <div className="braking_energy_imgbox">
          <ImageSelector
            className="braking_energy_img"
            alt="braking_energy_img"
            kiaLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/car_management/braking_energy_nodata.png')}
          />
        </div>
      </div>
    </div>
  );
}

export function BrakeEnergyContentsLoading() {
  return (
    <div
      className="section_container skeleton"
      style={{height: 313}}
    />
  );
}
